import moment, { Moment } from "moment"

// Used when the product's brokerMaxCancellationBackdateDays setting is
// missing or invalid. Matches the API's default.
export const DEFAULT_BROKER_MAX_BACKDATE_DAYS = 30

// Larger settings are treated as a typo and fall back to the default, as the
// API does.
export const MAX_BROKER_MAX_BACKDATE_DAYS = 3650

// Compare dates by the calendar day they represent in their own mode (UTC or
// local), not by instant, so a UTC-midnight value and a local picker cell for
// the same day are treated as equal.
const calendarDay = (date: Moment): string => date.format("YYYY-MM-DD")

/**
 * The earliest effective cancellation date a broker may choose, as UTC
 * midnight of the calendar day `maxBackdateDays` before the user's local
 * "today". `now` defaults to the current time, so callers that evaluate this
 * on each render get a window that moves forward at midnight.
 */
export const getBrokerEarliestCancellationDate = (
  maxBackdateDays: number,
  now: Moment = moment()
): Moment =>
  moment.utc(
    calendarDay(now.clone().startOf("day").subtract(maxBackdateDays, "days"))
  )

export const isBeforeBrokerBackdateLimit = (
  date: Moment,
  maxBackdateDays: number,
  now: Moment = moment()
): boolean =>
  calendarDay(date) <
  calendarDay(getBrokerEarliestCancellationDate(maxBackdateDays, now))

/**
 * Whether a date picker cell should be disabled for an effective cancellation
 * date: outside the policy's cancellable window for everyone, or further back
 * than `brokerMaxBackdateDays` for brokers. `brokerMaxBackdateDays` is
 * undefined for users who aren't brokers, who aren't restricted.
 */
export const isCancellationDateDisabled = (
  current: Moment,
  {
    minCancellationDate,
    coverEndDate,
    brokerMaxBackdateDays,
    now = moment()
  }: {
    minCancellationDate?: Moment
    coverEndDate?: Moment
    brokerMaxBackdateDays?: number
    now?: Moment
  }
): boolean => {
  if (!minCancellationDate || !coverEndDate) {
    return true
  }

  const outsidePolicyWindow = !(
    minCancellationDate.isSameOrBefore(current) &&
    coverEndDate.isSameOrAfter(current)
  )

  return (
    outsidePolicyWindow ||
    (brokerMaxBackdateDays !== undefined &&
      isBeforeBrokerBackdateLimit(current, brokerMaxBackdateDays, now))
  )
}

/**
 * True when a broker has no selectable effective date at all because the
 * policy's cover ended before the backdating limit.
 */
export const brokerHasNoSelectableCancellationDate = (
  coverEndDate: Moment | undefined,
  brokerMaxBackdateDays: number | undefined,
  now: Moment = moment()
): boolean =>
  brokerMaxBackdateDays !== undefined &&
  !!coverEndDate &&
  isBeforeBrokerBackdateLimit(coverEndDate, brokerMaxBackdateDays, now)

/**
 * Reads the product's setting, falling back to the default when it is
 * missing (e.g. an older API) or not a whole number from 0 to
 * MAX_BROKER_MAX_BACKDATE_DAYS.
 */
export const resolveBrokerMaxBackdateDays = (setting: unknown): number =>
  typeof setting === "number" &&
  Number.isInteger(setting) &&
  setting >= 0 &&
  setting <= MAX_BROKER_MAX_BACKDATE_DAYS
    ? setting
    : DEFAULT_BROKER_MAX_BACKDATE_DAYS

const dayCount = (days: number): string =>
  `${days} ${days === 1 ? "day" : "days"}`

export const brokerBackdateLimitMessage = (days: number): string =>
  `The effective cancellation date can't be more than ${dayCount(
    days
  )} in the past.`

/**
 * True when the backdating limit disables dates a broker could otherwise pick,
 * i.e. the policy's cancellable window starts before the limit. When it
 * doesn't (e.g. cover started recently), older dates are disabled by the
 * policy window for everyone, so explaining the limit would mislead.
 */
export const brokerBackdateLimitRestrictsDates = (
  minCancellationDate: Moment | undefined,
  brokerMaxBackdateDays: number | undefined,
  now: Moment = moment()
): boolean =>
  brokerMaxBackdateDays !== undefined &&
  !!minCancellationDate &&
  isBeforeBrokerBackdateLimit(minCancellationDate, brokerMaxBackdateDays, now)

// Shown to brokers under the effective date picker so they know why older
// dates can't be selected before they run into it.
export const brokerBackdateLimitInfoMessage = (days: number): string =>
  days === 0
    ? "You cannot backdate a cancellation; dates before today cannot be selected. If you wish to backdate, please discuss this with Stella support."
    : `You can backdate a cancellation by a maximum of ${dayCount(
        days
      )}; earlier dates cannot be selected. If you wish to backdate to an earlier date, please discuss this with Stella support.`

// The amendment version: a broker may keep an existing effective date that is
// already older than the limit, so only moving it is restricted.
export const brokerAmendmentBackdateInfoMessage = (days: number): string =>
  `You can keep the current effective date, or choose ${
    days === 0
      ? "today or a later date"
      : `a date up to a maximum of ${dayCount(days)} in the past`
  }; earlier dates cannot be selected. If you wish to backdate to an earlier date, please discuss this with Stella support.`

export const brokerAmendmentDateLockedMessage = (days: number): string =>
  `This policy's cover ended more than ${dayCount(
    days
  )} ago, so the effective date can't be changed. You can still update the other cancellation details.`

export const brokerNoSelectableDateMessage = (days: number): string =>
  `This policy's cover ended more than ${dayCount(
    days
  )} ago, so it can't be cancelled from the broker portal. Please contact Stella to cancel it.`
